/**
 * Ant Design ConfigProvider 테마 — 토큰 기반 빌드
 *
 * 밀도(높이·패딩·글자 크기)의 정본은 **여기**다 (2026-09-03 밀도 키트 v3).
 * 이전에는 index.css 말미의 `!important` 블록이 밀도를 정했는데,
 *  ① 프로젝트 규칙끼리 특이도 싸움이 났고(.ant-menu-item 만 겨냥하면 .ant-menu-inline .ant-menu-item 에 진다)
 *  ② 페이지가 준 인라인 패딩을 덮어써 오히려 여백이 늘어나는 역행이 있었다.
 * antd 6 은 이 값들을 정식 컴포넌트 토큰으로 노출하므로 CSS 에는
 * **토큰으로 표현할 수 없는 것(색·배경·액센트·선택 강조)만** 남긴다.
 */
import type { ThemeConfig } from 'antd';
import { brand, gray, state, font, radius, shadow, color, space, layout, density } from './tokens';

export { brand, gray, state, font, radius, shadow, color, space, layout, density, tokens } from './tokens';

export const antdTheme: ThemeConfig = {
  token: {
    // Brand
    colorPrimary: brand.primary,
    colorInfo: brand.primary,
    colorLink: brand.primary,
    colorSuccess: state.success,
    colorWarning: state.warning,
    colorError: state.error,

    // Typography — 콘솔 밀도의 기본 눈금
    fontSize: density.fontSize,
    lineHeight: density.lineHeight,
    fontFamily: font.family,

    // Control — 버튼·입력·셀렉트·날짜선택이 모두 이 하나를 본다
    controlHeight: density.controlHeight,
    controlHeightSM: density.controlHeightSM,

    // Radius — 둥글수록 장식으로 읽힌다. 계측기 쪽으로 한 단계
    borderRadius: radius.sm,
    borderRadiusLG: radius.md,
    borderRadiusSM: 4,

    // Spacing
    padding: space.base,
    paddingLG: space.lg,

    // Background
    colorBgContainer: color.bg,
    colorBgLayout: color.bgLayout,
    colorBgElevated: color.bgElevated,

    // Border
    colorBorder: color.border,
    colorBorderSecondary: color.borderSubtle,

    // Text
    colorText: color.text,
    colorTextSecondary: color.textSecondary,
    colorTextTertiary: color.textTertiary,
    colorTextQuaternary: gray[300],

    // Shadow
    boxShadow: shadow.md,
    boxShadowSecondary: shadow.lg,
  },
  components: {
    Layout: {
      headerHeight: layout.headerHeight,
      headerPadding: '0 14px',
    },
    Card: {
      headerBg: color.bg,
      headerHeight: density.cardHeaderHeight,
      headerHeightSM: 28,
      headerPadding: density.cardHeaderPadding,
      headerFontSize: density.fontSize,
      headerFontSizeSM: density.fontSizeSmall,
      /* 인라인 padding:0(전폭 표)을 이기지 않는다 — 토큰은 antd 기본값 자리에 들어가므로
         CSS !important 로 덮던 때와 달리 페이지의 의도를 존중한다 */
      bodyPadding: density.cardBodyPadding,
      bodyPaddingSM: 8,
      paddingLG: space.lg,
    },
    Table: {
      headerBg: gray[50],
      headerColor: gray[600],
      rowHoverBg: gray[50],
      borderColor: gray[100],
      /* 세 크기(기본/MD/SM)를 모두 건다 — SM 만 걸면 size 를 지정하지 않은 표가
         antd 기본(12~16px)으로 되돌아가 행이 31 → 47px 로 역행한다
         (2026-09-03 로컬 빌드 실측에서 ADB 데모·점검일지·파트너 시험일정이 실제로 잡혔다) */
      cellPaddingBlock: density.tableCellPaddingBlock,
      cellPaddingBlockMD: density.tableCellPaddingBlock,
      cellPaddingBlockSM: density.tableCellPaddingBlock,
      cellPaddingInline: density.tableCellPaddingInline,
      cellPaddingInlineMD: density.tableCellPaddingInline,
      cellPaddingInlineSM: density.tableCellPaddingInline,
      cellFontSize: density.fontSize,
      cellFontSizeMD: density.fontSize,
      cellFontSizeSM: density.fontSize,
    },
    Menu: {
      darkItemBg: 'transparent',
      darkItemSelectedBg: 'rgba(99,102,241,0.35)',
      darkItemSelectedColor: '#ffffff',
      darkItemHoverBg: 'rgba(255,255,255,0.08)',
      darkItemHoverColor: '#ffffff',
      darkItemColor: 'rgba(203,213,235,0.75)',
      darkSubMenuItemBg: 'transparent',
      itemSelectedBg: brand.primarySoftBg,
      itemSelectedColor: brand.primary,
      itemHoverBg: 'rgba(79,70,229,0.04)',
      itemHoverColor: brand.primary,
      // 밀도 — 27개 항목이 쌓이므로 항목 하나의 8px 이 216px 이 된다
      itemHeight: density.menuItemHeight,
      itemMarginBlock: density.menuItemMarginBlock,
      itemMarginInline: density.menuItemMarginInline,
      itemPaddingInline: density.menuItemPaddingInline,
      itemBorderRadius: radius.sm,
      iconSize: density.menuIconSize,
      collapsedIconSize: 16,
      collapsedWidth: layout.sidebarCollapsed,
      groupTitleFontSize: 10,
    },
    Button: {
      primaryShadow: brand.primaryShadow,
      defaultBorderColor: color.border,
      paddingInline: 10,
    },
    Input: {
      activeBorderColor: brand.primary,
      hoverBorderColor: brand.primaryHover,
      activeShadow: `0 0 0 3px ${brand.primaryRing}`,
    },
    Form: {
      labelColor: gray[600],
      labelFontSize: font.size.sm,
      labelHeight: density.formLabelHeight,
      itemMarginBottom: density.formItemMarginBottom,
      verticalLabelPadding: '0 0 2px',
    },
    Tabs: {
      inkBarColor: brand.primary,
      itemActiveColor: brand.primary,
      itemSelectedColor: brand.primary,
      itemHoverColor: brand.primaryHover,
      titleFontSize: density.fontSize,
      horizontalItemPadding: '5px 4px',
      horizontalMargin: `0 0 6px 0`,
      cardHeight: 30,
      cardPadding: '4px 12px',
    },
    Modal: {
      /* antd 6 의 Modal 공개 토큰은 색·제목뿐이다 — 본문 패딩은 index.css 에서 정한다 */
      titleFontSize: 15,
      titleLineHeight: 1.4,
    },
    Descriptions: {
      labelBg: gray[50],
      itemPaddingBottom: 6,
    },
    Pagination: {
      itemSize: 24,
    },
    Segmented: {
      trackPadding: 2,
    },
    List: {
      itemPadding: '8px 0',
      itemPaddingSM: '6px 0',
    },
  },
};
